import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { sendContractTransaction, approveToken, formatTransactionError } from '../utils/transactionHelper';
import './OrderManagement.css';

const OrderManagement = () => {
  const web3Context = useWeb3();
  const { account, contract, web3, usdtContract, CONTRACT_ADDRESS, USDT_ADDRESS } = web3Context;

  const [myOrders, setMyOrders] = useState({ sell: [], buy: [] });
  const [activeTab, setActiveTab] = useState('sell');
  const [loading, setLoading] = useState(false);
  const [swapAmount, setSwapAmount] = useState('');
  const [swapping, setSwapping] = useState(false);
  const [tokenPrice, setTokenPrice] = useState('0');
  const [usdtBalance, setUsdtBalance] = useState('0');

  useEffect(() => {
    if (account && contract) {
      loadMyOrders();
      loadTokenPrice();
      loadUsdtBalance();
    }
  }, [account, contract]);

  const loadUsdtBalance = async () => {
    try {
      if (usdtContract && account) {
        const bal = await usdtContract.methods.balanceOf(account).call();
        setUsdtBalance(web3.utils.fromWei(bal.toString(), 'ether'));
      }
    } catch (error) {
      console.error('Error loading USDT balance:', error);
    }
  };

  const loadTokenPrice = async () => {
    try {
      const price = await contract.methods.tokenPerUSDT().call();
      setTokenPrice(price.toString());
    } catch (error) {
      console.error('Error loading token price:', error);
    }
  };

  const loadMyOrders = async () => {
    setLoading(true);
    try {
      // 0 = SELL order type enum
      const sellOrderIds = await contract.methods.getMyOrders(account, 0).call();
      const sellOrders = [];
      if (sellOrderIds && sellOrderIds.length > 0) {
        for (const orderId of sellOrderIds) {
          const rawOrder = await contract.methods.orders(orderId).call();
          if (!rawOrder) continue;

          const id = rawOrder.id !== undefined ? rawOrder.id : rawOrder[0];
          const seller = rawOrder.seller !== undefined ? rawOrder.seller : rawOrder[1];
          const totalTokens = rawOrder.totalTokens !== undefined ? rawOrder.totalTokens : rawOrder[2];
          const remainingTokens = rawOrder.remainingTokens !== undefined ? rawOrder.remainingTokens : rawOrder[3];
          const price = rawOrder.price !== undefined ? rawOrder.price : rawOrder[4];
          const bonusPercent = rawOrder.bonusPercent !== undefined ? rawOrder.bonusPercent : rawOrder[5];
          const feeBNB = rawOrder.feeBNB !== undefined ? rawOrder.feeBNB : rawOrder[6];
          const active = rawOrder.active !== undefined ? rawOrder.active : rawOrder[7];

          const totalTokensStr = totalTokens ? totalTokens.toString() : '0';
          const remainingTokensStr = remainingTokens ? remainingTokens.toString() : '0';
          const totalFormatted = web3.utils.fromWei(totalTokensStr, 'ether');
          const remainingFormatted = web3.utils.fromWei(remainingTokensStr, 'ether');
          const soldWei = window.BigInt(totalTokensStr) - window.BigInt(remainingTokensStr);
          const soldFormatted = web3.utils.fromWei(soldWei.toString(), 'ether');
          const priceNum = parseFloat(price ? price.toString() : '1') || 1;

          sellOrders.push({
            id: (id || orderId).toString(),
            seller: seller ? seller.toString() : '',
            totalTokens: totalFormatted,
            remainingTokens: remainingFormatted,
            soldTokens: soldFormatted,
            price: price ? price.toString() : '1',
            bonusPercent: bonusPercent ? bonusPercent.toString() : '0',
            feeBNB: web3.utils.fromWei(feeBNB ? feeBNB.toString() : '0', 'ether'),
            active: Boolean(active),
            usdtPerToken: (1 / priceNum).toFixed(6)
          });
        }
      }

      // 1 = BUY order type enum
      const buyOrderIds = await contract.methods.getMyOrders(account, 1).call();
      const buyOrders = [];
      if (buyOrderIds && buyOrderIds.length > 0) {
        for (const orderId of buyOrderIds) {
          const rawOrder = await contract.methods.orders(orderId).call();
          if (!rawOrder) continue;

          const id = rawOrder.id !== undefined ? rawOrder.id : rawOrder[0];
          const seller = rawOrder.seller !== undefined ? rawOrder.seller : rawOrder[1];
          const totalTokens = rawOrder.totalTokens !== undefined ? rawOrder.totalTokens : rawOrder[2];
          const price = rawOrder.price !== undefined ? rawOrder.price : rawOrder[4];
          const bonusPercent = rawOrder.bonusPercent !== undefined ? rawOrder.bonusPercent : rawOrder[5];

          const priceNum = parseFloat(price ? price.toString() : '1') || 1;

          buyOrders.push({
            id: (id || orderId).toString(),
            seller: seller ? seller.toString() : '',
            totalTokens: web3.utils.fromWei(totalTokens ? totalTokens.toString() : '0', 'ether'),
            price: price ? price.toString() : '1',
            bonusPercent: bonusPercent ? bonusPercent.toString() : '0',
            usdtPerToken: (1 / priceNum).toFixed(6)
          });
        }
      }

      setMyOrders({ sell: sellOrders, buy: buyOrders });
    } catch (error) {
      console.error('Error loading user order history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm(`Are you sure you want to cancel Order #${orderId}? Remaining escrow tokens will be refunded to your wallet.`)) {
      return;
    }

    try {
      const method = contract.methods.cancelOrder(orderId);
      await sendContractTransaction(web3Context, method);

      alert(`Order #${orderId} cancelled successfully!`);
      loadMyOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert('Failed to cancel order: ' + formatTransactionError(error));
    }
  };

  const handleSwapUSDT = async () => {
    const swapNum = parseFloat(swapAmount);
    if (isNaN(swapNum) || swapNum <= 0) {
      alert('Please enter a valid positive USDT amount to swap');
      return;
    }

    if (swapNum > parseFloat(usdtBalance)) {
      alert(`Insufficient USDT balance! You have ${usdtBalance} USDT.`);
      return;
    }

    if (!tokenPrice || tokenPrice === '0') {
      alert('Swap unavailable: Token exchange price is not set by admin.');
      return;
    }

    setSwapping(true);
    try {
      const amountWei = web3.utils.toWei(swapAmount, 'ether');

      // Step 1: Approve USDT transfer to contract
      await approveToken(web3Context, USDT_ADDRESS, CONTRACT_ADDRESS, amountWei);

      // Step 2: Swap USDT for Tokens
      const method = contract.methods.swapUSDT(amountWei);
      await sendContractTransaction(web3Context, method);

      const tokensReceived = swapNum * parseFloat(tokenPrice);
      alert(`Swap successful! Received ${tokensReceived.toLocaleString()} Tokens.`);
      setSwapAmount('');
      loadUsdtBalance();
    } catch (error) {
      console.error('Error swapping USDT:', error);
      alert('Swap failed: ' + formatTransactionError(error));
    } finally {
      setSwapping(false);
    }
  };

  const calculateTokensReceived = () => {
    if (!swapAmount || isNaN(parseFloat(swapAmount)) || !tokenPrice || tokenPrice === '0') return '0.00';
    const tokens = parseFloat(swapAmount) * parseFloat(tokenPrice);
    return tokens.toLocaleString();
  };

  return (
    <div className="order-mgmt-wrapper">
      {/* Header Banner */}
      <div className="mgmt-header">
        <div className="title-box">
          <h2>Order History & Instant Swap</h2>
          <p>Track your active sell orders, review buy history, and execute instant USDT swaps.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mgmt-tabs">
        <button
          className={`tab-btn ${activeTab === 'sell' ? 'active' : ''}`}
          onClick={() => setActiveTab('sell')}
        >
          🏷️ My Sell Orders ({myOrders.sell.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'buy' ? 'active' : ''}`}
          onClick={() => setActiveTab('buy')}
        >
          🛒 My Buy History ({myOrders.buy.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'swap' ? 'active' : ''}`}
          onClick={() => setActiveTab('swap')}
        >
          ⚡ Instant USDT Swap
        </button>
      </div>

      {loading ? (
        <div className="loading-card">
          <div className="spinner"></div>
          <p>Fetching your order records...</p>
        </div>
      ) : (
        <>
          {/* Sell Orders Tab */}
          {activeTab === 'sell' && (
            <div className="orders-section-grid">
              {myOrders.sell.length === 0 ? (
                <div className="empty-box">
                  <div className="icon">📦</div>
                  <h4>No Active Sell Orders</h4>
                  <p>You haven't listed any sell orders yet. Visit "Create Sell Order" to list tokens.</p>
                </div>
              ) : (
                myOrders.sell.map((order) => (
                  <div key={order.id} className={`order-manage-card ${order.active ? 'active-order' : 'closed-order'}`}>
                    <div className="card-top">
                      <div className="order-id">
                        Order #{order.id}
                        <span className={`status-badge ${order.active ? 'active' : 'inactive'}`}>
                          {order.active ? 'Active Open' : 'Cancelled / Filled'}
                        </span>
                      </div>
                      <span className="bonus-pill">+{order.bonusPercent}% Bonus</span>
                    </div>

                    <div className="card-stats">
                      <div className="s-item">
                        <span>Total Listed:</span>
                        <span className="val">{parseFloat(order.totalTokens).toLocaleString()} Tokens</span>
                      </div>
                      <div className="s-item">
                        <span>Remaining Tokens:</span>
                        <span className="val highlight">{parseFloat(order.remainingTokens).toLocaleString()} Tokens</span>
                      </div>
                      <div className="s-item">
                        <span>Already Sold:</span>
                        <span className="val">{parseFloat(order.soldTokens).toLocaleString()} Tokens</span>
                      </div>
                      <div className="s-item">
                        <span>Rate:</span>
                        <span className="val">1 USDT = {order.price} Tokens</span>
                      </div>
                    </div>

                    {order.active && parseFloat(order.remainingTokens) > 0 && (
                      <button
                        className="btn-cancel-order"
                        onClick={() => handleCancelOrder(order.id)}
                      >
                        Cancel & Refund Tokens
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Buy History Tab */}
          {activeTab === 'buy' && (
            <div className="orders-section-grid">
              {myOrders.buy.length === 0 ? (
                <div className="empty-box">
                  <div className="icon">🛒</div>
                  <h4>No Buy Order History</h4>
                  <p>Purchased orders will appear here after executing trades in the Marketplace.</p>
                </div>
              ) : (
                myOrders.buy.map((order, idx) => (
                  <div key={idx} className="order-manage-card buy-card">
                    <div className="card-top">
                      <span className="order-id">Buy Execution on Order #{order.id}</span>
                      <span className="bonus-pill">+{order.bonusPercent}% Bonus</span>
                    </div>
                    <div className="card-stats">
                      <div className="s-item">
                        <span>Seller Address:</span>
                        <span className="code">{order.seller.slice(0, 8)}...{order.seller.slice(-6)}</span>
                      </div>
                      <div className="s-item">
                        <span>Rate:</span>
                        <span className="val">1 USDT = {order.price} Tokens</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Instant Swap Tab */}
          {activeTab === 'swap' && (
            <div className="swap-card-container">
              <div className="swap-header">
                <h3>⚡ Direct Contract USDT Swap</h3>
                <p>Convert USDT directly into target tokens from contract balance without using open orders.</p>
                <div className="rate-chip">
                  Current Exchange Rate: <strong>1 USDT = {tokenPrice} Tokens</strong>
                </div>
              </div>

              <div className="swap-form">
                <div className="swap-input-group">
                  <div className="label-row">
                    <label>USDT Amount to Exchange</label>
                    <span className="bal-txt">Balance: {parseFloat(usdtBalance).toLocaleString()} USDT</span>
                  </div>
                  <div className="input-field">
                    <input
                      type="number"
                      placeholder="0.00"
                      value={swapAmount}
                      onChange={(e) => setSwapAmount(e.target.value)}
                      disabled={swapping}
                    />
                    <button className="max-btn" onClick={() => setSwapAmount(usdtBalance)}>MAX</button>
                  </div>
                </div>

                {swapAmount && parseFloat(swapAmount) > 0 && (
                  <div className="swap-estimate-box">
                    <span>You will receive approximately:</span>
                    <strong className="tokens-received">{calculateTokensReceived()} Tokens</strong>
                  </div>
                )}

                <button
                  className="btn-execute-swap"
                  onClick={handleSwapUSDT}
                  disabled={swapping || !swapAmount || parseFloat(swapAmount) <= 0}
                >
                  {swapping ? 'Swapping USDT...' : 'Approve USDT & Swap Tokens'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrderManagement;